/**
 * The visitor's marketing-consent decision, as recorded by the consent banner
 * in the `_ms_consent` cookie.
 *
 * Why this leaves the browser at all: the audience sync uploads a buyer's
 * HASHED identifiers into Meta Custom Audiences, and under
 * `AUDIENCE_SYNC_CONSENT_POLICY=geo_gated` an EEA/UK/CH buyer may only be
 * uploaded when a granted consent record exists for them. The banner's decision
 * lives in a first-party cookie on the funnel domain; the Go API is on a
 * different host and never receives that cookie, so the checkout payload has to
 * carry the decision explicitly. The server materialises it into a real
 * `consent_records` row once the order resolves to a contact
 * (handler/consent_capture.go).
 *
 * Trust level is identical to `ad_identifiers`, which the same payloads already
 * carry: client-reported, and only ever able to describe the visitor's own
 * session. A forged "granted" is a visitor claiming consent they did give in
 * another tab; a forged "denied" costs us one audience member. Neither is worth
 * a signed cookie.
 */

/** Cookie values the banner and the tag loaders write. */
export type ConsentState = "granted" | "denied" | "";

/** Order-metadata key the Go side reads (handler.orderConsentMetadataKey). */
export const CONSENT_METADATA_KEY = "ms_consent";

function readCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : "";
}

/**
 * Normalises `_ms_consent` to the two states the server understands. The tag
 * loaders treat a legacy `marketing` value as granted (see meta-pixel.ts), so
 * this does too. Anything else — no cookie at all, an unknown value — is "",
 * which the server records as NO decision rather than as a denial: absent
 * consent already denies under geo_gated, and inventing a "denied" record would
 * wrongly claim the visitor was asked.
 */
export function readConsentState(): ConsentState {
  const c = readCookie("_ms_consent");
  if (c === "granted" || c === "marketing") return "granted";
  if (c === "denied") return "denied";
  return "";
}

/**
 * Spread into an order-creation `metadata` object:
 *   metadata: { slug, ...consentMetadata() }
 * Contributes nothing when the visitor never saw or answered the banner.
 */
export function consentMetadata(): Record<string, string> {
  const state = readConsentState();
  return state ? { [CONSENT_METADATA_KEY]: state } : {};
}
